/**
 * Teslyn Field's release feed and going back to an earlier version (parity 06-050, decision 109;
 * docs/specs/field-updates.md). Each channel's feed has `updates/<channel>/latest.json` (what the updater installs)
 * and `updates/<channel>/history.json` (the channel's latest releases, newest first, written by
 * `tools/release/publish-update.mjs` from the release workflow). Teslyn Field offers the releases older than itself,
 * at most `ROLLBACK_KEEP` of them; the core checks the choice again and verifies the installer's signature before
 * anything runs (`apps/field/src-tauri/src/update.rs`). The numbers are in `release-feed.json`, read by the core and
 * the release tool too.
 */
import feed from './release-feed.json';

/** How many earlier releases Teslyn Field offers to go back to (decision 109: the last 5). */
export const ROLLBACK_KEEP: number = feed.rollback_keep;
/** How many releases the feed history keeps: the current one and the ones to go back to. */
export const RELEASE_HISTORY_KEEP: number = feed.rollback_keep + 1;
/** The `v` of `history.json`. */
export const RELEASE_HISTORY_VERSION: number = feed.history_version;

/** `YYYY.MM.patch` (ADR-014), with or without a leading `v`; null for anything else. */
export function parseReleaseVersion(v: string): [number, number, number] | null {
  const m = /^v?(\d{4})\.(\d{1,2})\.(\d+)$/.exec(v.trim());
  if (!m) return null;
  const [y, mo, p] = [Number(m[1]), Number(m[2]), Number(m[3])];
  if (m[2]!.length === 2 && m[2]!.startsWith('0')) return null;
  return [y, mo, p];
}

/** Negative when `a` is older, positive when newer, 0 when the same; null when either is not a release version. */
export function compareReleaseVersions(a: string, b: string): number | null {
  const x = parseReleaseVersion(a);
  const y = parseReleaseVersion(b);
  if (!x || !y) return null;
  for (let i = 0; i < 3; i++) if (x[i] !== y[i]) return x[i]! - y[i]!;
  return 0;
}

/**
 * What going back does to the projects on the laptop. Nothing is deleted or changed, but a project that a newer
 * version has opened carries that version's database (`teslyn-db` schema version), which an older version refuses
 * to open ("Update Teslyn Field"). `same`: every project opens. `newer_projects_closed`: the projects this version
 * has opened stay closed in the older one until it is updated again. `unknown`: the release did not record its
 * database version, so that may happen.
 */
export type RollbackProjectEffect = 'same' | 'newer_projects_closed' | 'unknown';

export function rollbackProjectEffect(
  targetSchema: number | null,
  currentSchema: number,
): RollbackProjectEffect {
  if (targetSchema === null || targetSchema > currentSchema) return 'unknown';
  return targetSchema === currentSchema ? 'same' : 'newer_projects_closed';
}
